import { useVibe } from '@/features/colors/hooks/useVibe';
import {
  useEffectiveExperience,
  usePageExperienceContext,
} from '@/features/experiences/contexts/PageExperienceContext';
import { useUpdateExperience } from '@/features/experiences/hooks/useUpdateExperience';
import {
  getEventCardCapabilities,
  isCalendarLayout,
  rendersDayColumns,
  rendersWeekStart,
  isListLayout,
  rendersColorLegend,
  rendersWeekendCells,
  supportsEventDisplayControls,
} from '@/features/layouts/layout.lib';
import { shouldUseBlockPrintableRenderer } from '@/features/printables/renderer.lib';
import { useAnalytics } from '@/hooks/useAnalytics';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import {
  EventCardLayouts,
  type EventListDisplay,
  ExperienceChildConfigKind,
} from '@/types/gql.generated';
import { Box } from '@/ui';
import { useCustomizeControl } from '../useCustomizeControl';
import { useRememberedEventListWindow } from '../useRememberedEventListWindow';
import { DayColumnsSection } from './DayColumnsSection';
import { DisplaySection } from './DisplaySection';
import {
  shouldShowCardColorMode,
  shouldShowDatesInPrimaryColor,
  shouldShowHighlightToday,
} from './displaySettings.lib';
import { HeaderSection } from './HeaderSection';
import { ShowOnViewSection } from './ShowOnViewSection';
import { WhatToShowSection } from './WhatToShowSection';

/**
 * The four view-settings sections. Reads from whatever PageExperienceContext is
 * in scope — when wrapped in ChildExperienceLoader, that's the active child view;
 * for single experiences (no children), it's the experience itself.
 */
export const ViewSettingsSections = () => {
  const { trackEvent } = useAnalytics();
  const { experience } = usePageExperienceContext();
  const { register } = useCustomizeControl();
  const effectiveExperience = useEffectiveExperience();
  const { supportsDisplaySetting } = useVibe();
  const { value: printableBlockEnabled } = useFeatureFlag(
    'printableBlockEnabled'
  );
  const { value: whatToShowSectionEnabled } = useFeatureFlag(
    'whatToShowSectionEnabled'
  );
  const { setBlockLayout, setEventListWindow, setTitle } =
    useUpdateExperience();
  const rememberedWindowFor = useRememberedEventListWindow(experience);

  // A freshly created interactive planner child comes back with
  // eventCardLayoutId = None, which matches no cardOption and would hide every
  // "Show on this view" toggle until an edit reassigns it. The planner event
  // items read the eventCard*Enabled flags directly (not the card layout), so
  // fall back to the layout's default card option to surface the toggles.
  const effectiveCardLayoutId =
    experience.eventCardLayoutId === EventCardLayouts.None
      ? (experience.layout.cardOptions[0]?.layout ??
        experience.eventCardLayoutId)
      : experience.eventCardLayoutId;

  const cardCapabilities = getEventCardCapabilities(
    experience.layout,
    effectiveCardLayoutId
  );

  // The display-type picker (card/line/bullet/text) shows only where the server
  // offers more than one card option — the planner grids. Both the display type
  // and text size persist on the experience (eventCardLayoutId + eventTextSize)
  // so they drive interactive and printable identically.
  const showDisplayType = supportsEventDisplayControls(experience.layout);

  // Classic printable templates remain styleId-driven, so they cannot honor a
  // vibe-owned setting. Interactive views are always vibe-aware; printable views
  // join them only on the block renderer.
  const usesVibeRenderer =
    experience.kind !== ExperienceChildConfigKind.Printable ||
    shouldUseBlockPrintableRenderer(printableBlockEnabled, effectiveExperience);

  // Sits alongside the display-type picker so the card options stay together. colorMode
  // is whole-schedule: it reads from (and writes to) the parent planner, which the scope
  // caption on the control says out loud. The Style panel offers the same setting over
  // its sample gallery; both bind the one field, so they never disagree.
  //
  // Month Cards is in even though it draws no event cards, but only on the vibe
  // renderer — see shouldShowCardColorMode for why that gate applies to the month-card
  // branch and not the grid one.
  const showCardColorMode = shouldShowCardColorMode({
    cardLayoutId: effectiveCardLayoutId,
    layoutId: experience.layoutId,
    showDisplayType,
    usesVibeRenderer,
  });

  // Text size is a calendar-only control (Month / Week grids + Month Cards). The
  // color + truncation options also cover the list layouts (Cards / List) and the
  // single-card calendar layouts (Month Cards), which have one fixed card option
  // so showDisplayType is false for them — hence the explicit calendar/list gates.
  const showTextSize = isCalendarLayout(experience.layoutId);
  const showWeekStart = rendersWeekStart(experience.layoutId);
  const showEventOptions =
    showDisplayType ||
    isListLayout(experience.layoutId) ||
    isCalendarLayout(experience.layoutId);
  // The color legend is meaningful wherever a layout draws one — every calendar
  // grid, not just the multi-card ones the display-type control gates, plus the
  // printable list, whose ListFooter renders the same Legend. showDisplayType stays
  // in the condition so a multi-card layout outside those families keeps its toggle.
  const showColorLegend =
    !rendersDayColumns(experience.layoutId) &&
    (showDisplayType || rendersColorLegend(experience.layoutId));

  // Weekend styling only exists where a layout renders weekend day cells:
  // interactive + printable Month/Week and Week Columns, but NOT Month Cards
  // (calendar-categorized but a per-box event list with no day-of-week grid).
  const showShadeWeekends = rendersWeekendCells(experience.layoutId);
  const showMuteWeekendDates = showShadeWeekends;

  // Ungated, the toggle showed on the Printable List and Month Cards, which have no
  // day cell to mark — writing highlightToday with nothing rendering it.
  const showHighlightToday = shouldShowHighlightToday({
    kind: experience.kind,
    layoutId: experience.layoutId,
    supportedByVibe: supportsDisplaySetting('highlightToday'),
  });

  const showDatesInPrimaryColor = shouldShowDatesInPrimaryColor({
    layoutId: experience.layoutId,
    supportedByVibe: supportsDisplaySetting('datesInPrimaryColor'),
    usesVibeRenderer,
  });

  return (
    <Box w="100%">
      {rendersDayColumns(experience.layoutId) && (
        <DayColumnsSection
          experience={experience}
          onSetBlockLayout={setBlockLayout}
        />
      )}

      <ShowOnViewSection
        showCardColorMode={showCardColorMode}
        showColorLegend={showColorLegend}
        showDisplayType={showDisplayType}
        showEventOptions={showEventOptions}
        showTextSize={showTextSize}
        supportsDate={cardCapabilities.supportsDate}
        supportsDescription={cardCapabilities.supportsDescription}
        supportsEmoji={cardCapabilities.supportsEmoji}
        supportsLocation={cardCapabilities.supportsLocation}
      />

      <DisplaySection
        experience={experience}
        showDatesInPrimaryColor={showDatesInPrimaryColor}
        showHighlightToday={showHighlightToday}
        showMuteWeekendDates={showMuteWeekendDates}
        showShadeWeekends={showShadeWeekends}
        showWeekStart={showWeekStart}
        onSetBlockLayout={setBlockLayout}
        onTrackGroupBy={(collectionLayout, pageLayout) =>
          trackEvent('experience:author change layout', {
            collectionLayout,
            pageLayout,
          })
        }
      />

      {/* Flag-gated while the eventListDisplay change rolls out. This branch is
          the change that gate was waiting on, so it is ready to retire — kept
          for now because that is a rollout call, not part of the fix. */}
      {whatToShowSectionEnabled && (
        <WhatToShowSection
          eventListDisplayOptions={experience.layout.eventListDisplayOptions}
          {...register('child', 'eventListDisplay', {
            // Which events the view shows changes the fetch, not just the render.
            // The dates move with the mode in one write, so the two are never seen
            // disagreeing — carrying the author's own window back into Date range
            // rather than dropping it.
            commit: (display) => {
              const { eventListStartDate, eventListEndDate } =
                rememberedWindowFor(display as EventListDisplay);
              setEventListWindow(
                display as EventListDisplay,
                eventListStartDate,
                eventListEndDate
              );
            },
          })}
        />
      )}

      <HeaderSection
        experience={experience}
        onSetBlockLayout={setBlockLayout}
        onSetTitle={setTitle}
      />
    </Box>
  );
};
